import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home, Wine, Truck, Building2, Grape, Package,
  Snowflake, BarChart3, Settings, LogOut,
  Menu, X, Store, Boxes, Calendar
} from 'lucide-react';

const MenuItem = ({ item, isActive, onNavigate, level = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = item.icon;
  const hasSubItems = item.subItems?.length > 0;

  return (
    <li className="nav-item">
      <button
        onClick={() => {
          if (hasSubItems) {
            setIsOpen(!isOpen);
          }
          if (!hasSubItems) {
            onNavigate(item.path);
          }
        }}
        className={`nav-link d-flex align-items-center justify-content-between w-100 ${
          isActive(item.path) ? 'active' : ''
        }`}
        style={{ paddingLeft: `${level * 1.5 + 1}rem` }}
      >
        <div className="d-flex align-items-center">
          {Icon && <Icon className="me-2" size={18} />}
          <span>{item.label}</span>
        </div>
        {hasSubItems && (
          <span className="ms-2">{isOpen ? '−' : '+'}</span>
        )}
      </button>
      
      {hasSubItems && isOpen && (
        <ul className="nav flex-column">
          {item.subItems.map((subItem) => (
            <MenuItem
              key={subItem.path}
              item={subItem}
              isActive={isActive}
              onNavigate={onNavigate}
              level={level + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const SideNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: Snowflake, label: 'Facility', path: '/facility' },
    { icon: Building2, label: 'Companies', path: '/companies' },
    {
      icon: Grape,
      label: 'Vineyards',
      path: '#',
      subItems: [
        { label: 'Vineyards', path: '/vineyards' },
        { label: 'Weight Tags', path: '/weight-tags' },
        { label: 'Harvest Plans', path: '/harvest-planning' }
      ]
    },
    {
      icon: Wine,
      label: 'Wines',
      path: '/wines',
      subItems: [
        { label: 'Wine List', path: '/wines' },
        { label: 'Work Orders', path: '/workorders' },
        { label: 'Blends', path: '/blends' },
        { label: 'Bottling Plans', path: '/bottling-plans' },
        { label: 'Containers', path: '/containers' }
      ]
    },
    { icon: Package, label: 'Cased Goods', path: '/bottled-wines' },
    { icon: Boxes, label: 'Materials', path: '/materials' },
    { icon: Truck, label: 'Shipping', path: '/bill-of-lading-list' },
    { icon: Store, label: 'Tasting Rooms', path: '/tasting-room' },
    {
      icon: BarChart3,
      label: 'Reports',
      path: '/reports',
      subItems: [
        { label: 'Tax Report', path: '/reports/tax-classes' }
      ]
    },
    { 
      icon: Settings, 
      label: 'Wine Settings',
      path: '/settings',
      subItems: [
        { label: 'Maintenance Settings', path: '/wine-settings' },
        { label: 'Tag Management', path: '/settings/tags' },
      ]
    }
  ];
 
  const footerItems = [
    { icon: Settings, label: 'Settings', path: '/settings' },
    { icon: LogOut, label: 'Logout', path: '/logout' }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNavigate = (path) => {
    if (path === '/logout') {
      handleLogout();
    } else {
      navigate(path);
      if (window.innerWidth < 992) {
        setIsExpanded(false);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <button
        className="btn btn-light d-lg-none position-fixed top-0 start-0 mt-2 ms-2"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ zIndex: 1031 }}
      >
        {isExpanded ? <X size={24} /> : <Menu size={24} />}
      </button>

      <nav 
        className={`sidebar bg-light border-end ${isExpanded ? 'show' : ''}`}
        style={{
          width: '250px',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          transition: 'transform 0.3s ease',
          transform: isExpanded ? 'translateX(0)' : 'translateX(-100%)',
          zIndex: 1030,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div className="p-3 border-bottom flex-shrink-0 brand">
          <img 
            src="/img/alcera.png" 
            alt="Alcera Logo" 
            className="img-fluid"
            style={{ maxWidth: '180px' }}
          />
        </div>

        <div className="d-flex flex-column flex-grow-1 overflow-auto">
          <div className="flex-grow-1 overflow-auto">
            <ul className="nav flex-column p-3">
              {menuItems.map((item) => (
                <MenuItem
                  key={item.path}
                  item={item}
                  isActive={isActive}
                  onNavigate={handleNavigate}
                />
              ))}
            </ul>
          </div>

          <div className="mt-auto border-top">
            <ul className="nav flex-column p-3">
              {footerItems.map((item) => (
                <MenuItem
                  key={item.path}
                  item={item}
                  isActive={isActive}
                  onNavigate={handleNavigate}
                />
              ))}
            </ul>
          </div>
        </div>
      </nav>
      {isExpanded && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark d-lg-none"
          style={{ opacity: 0.5, zIndex: 1029 }}
          onClick={() => setIsExpanded(false)}
        />
      )}

<style>{`
        @media (min-width: 992px) {
          .sidebar {
            transform: translateX(0) !important;
          }
        }
        .nav-link {
          color: inherit;
          text-decoration: none;
          padding: 0.5rem 1rem;
          border: none;
          background: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .nav-link:hover {
          background-color: rgba(0,0,0,.05);
        }
        .nav-link.active {
          color:rgb(43, 179, 174);
          background-color: rgba(70, 70, 70, 0.1);
        }

        .brand {
        background-color: #000000;
        }

        .sidebar {
          display: flex;
          flex-direction: column;
        }
        .sidebar > div:last-child {
          display: flex;
          flex-direction: column;
          min-height: 0;
          flex: 1;
        }
      `}</style>
    </>
  );
};

export default SideNavigation;